import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Save, RefreshCw, Globe, Clock, FileText, Database } from 'lucide-react';
import toast from 'react-hot-toast';

import { apiHelper } from '@services/api';
import Button from '@components/common/Button';
import Loading from '@components/common/Loading';

const cardClass = 'bg-white rounded-2xl shadow-xl border border-gray-100 p-6';

const SystemSettings = () => {
  const [settings, setSettings] = useState({});
  const queryClient = useQueryClient();

  // Fetch system settings
  const { data: systemSettings, isLoading } = useQuery(
    ['system-settings'],
    async () => {
      const response = await apiHelper.get('/settings/system');
      return response.data;
    },
    {
      onSuccess: (data) => {
        setSettings(data || {});
      },
      staleTime: 30000
    }
  );

  // Update settings mutation
  const updateSettingsMutation = useMutation(
    async (updatedSettings) => {
      const response = await apiHelper.put('/settings/system', updatedSettings);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['system-settings']);
        toast.success('Settings updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update settings');
      }
    }
  );

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    updateSettingsMutation.mutate(settings);
  };

  const handleReset = () => {
    if (systemSettings) {
      setSettings(systemSettings);
      toast.success('Settings reset to saved values');
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border p-8">
        <Loading size="lg" text="Loading system settings..." />
      </div>
    );
  }

  // Helper to safely parse ints with fallback
  const safeInt = (val, fallback = 0) => {
    const n = Number(val);
    return Number.isFinite(n) ? n : fallback;
  };

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28 }}
    >
      {/* General Settings */}
      <section className={cardClass}>
        <div className="flex items-center gap-3 mb-6">
          <Globe className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">General Settings</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">System Name</label>
            <input
              type="text"
              value={settings.system_name ?? 'RDX Ticketing System'}
              onChange={(e) => handleSettingChange('system_name', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
            <input
              type="text"
              value={settings.company_name ?? 'RoadExpress Freight'}
              onChange={(e) => handleSettingChange('company_name', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Timezone</label>
            <select
              value={settings.timezone ?? 'Asia/Kolkata'}
              onChange={(e) => handleSettingChange('timezone', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
              <option value="UTC">UTC</option>
              <option value="Asia/Dubai">Asia/Dubai (GST)</option>
              <option value="Europe/London">Europe/London (GMT)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
            <select
              value={settings.date_format ?? 'DD/MM/YYYY'}
              onChange={(e) => handleSettingChange('date_format', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
            <select
              value={settings.currency ?? 'INR'}
              onChange={(e) => handleSettingChange('currency', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="INR">Indian Rupee (₹)</option>
              <option value="USD">US Dollar ($)</option>
              <option value="EUR">Euro (€)</option>
              <option value="GBP">British Pound (£)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
            <select
              value={settings.language ?? 'en'}
              onChange={(e) => handleSettingChange('language', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="en">English</option>
              <option value="hi">Hindi</option>
              <option value="mr">Marathi</option>
              <option value="gu">Gujarati</option>
            </select>
          </div>
        </div>
      </section>

      {/* Ticket Settings */}
      <section className={cardClass}>
        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Ticket Settings</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Priority</label>
            <select
              value={settings.default_priority ?? 'medium'}
              onChange={(e) => handleSettingChange('default_priority', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Auto-Assignment</label>
            <select
              value={settings.auto_assignment ?? 'round_robin'}
              onChange={(e) => handleSettingChange('auto_assignment', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="none">No Auto Assignment</option>
              <option value="round_robin">Round Robin</option>
              <option value="least_loaded">Least Loaded</option>
              <option value="random">Random</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Ticket Number Format</label>
            <input
              type="text"
              value={settings.ticket_format ?? 'RDX-{YYYY}-{######}'}
              onChange={(e) => handleSettingChange('ticket_format', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="RDX-{YYYY}-{######}"
            />
            <p className="text-xs text-gray-500 mt-1">Use {'{YYYY}'} for year, {'{######}'} for sequence number</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Max File Upload Size (MB)</label>
            <input
              type="number"
              value={safeInt(settings.max_file_size, 10)}
              onChange={(e) => handleSettingChange('max_file_size', safeInt(e.target.value, 10))}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              min={1}
              max={100}
            />
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!settings.allow_customer_close}
                onChange={(e) => handleSettingChange('allow_customer_close', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Allow customers to close their own tickets</span>
            </label>
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!settings.require_approval}
                onChange={(e) => handleSettingChange('require_approval', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Require admin approval for ticket creation</span>
            </label>
          </div>
        </div>
      </section>

      {/* SLA Settings */}
      <section className={cardClass}>
        <div className="flex items-center gap-3 mb-6">
          <Clock className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">SLA Settings</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Business Hours Start</label>
            <input
              type="time"
              value={settings.business_hours_start ?? '09:00'}
              onChange={(e) => handleSettingChange('business_hours_start', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Business Hours End</label>
            <input
              type="time"
              value={settings.business_hours_end ?? '18:00'}
              onChange={(e) => handleSettingChange('business_hours_end', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Working Days</label>
            <div className="grid grid-cols-7 gap-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                const dayIndex = idx + 1; // 1..7
                const workingDays = settings.working_days ?? [1, 2, 3, 4, 5];
                const checked = workingDays.includes(dayIndex);
                return (
                  <label key={day} className="flex flex-col items-center">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const current = settings.working_days ?? [1, 2, 3, 4, 5];
                        if (e.target.checked) {
                          handleSettingChange('working_days', Array.from(new Set([...current, dayIndex])));
                        } else {
                          handleSettingChange('working_days', current.filter(d => d !== dayIndex));
                        }
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-xs text-gray-600 mt-1">{day}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.sla_exclude_weekends ?? true}
                onChange={(e) => handleSettingChange('sla_exclude_weekends', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Exclude weekends from SLA calculations</span>
            </label>
          </div>
        </div>
      </section>

      {/* System Maintenance */}
      <section className={cardClass}>
        <div className="flex items-center gap-3 mb-6">
          <Database className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">System Maintenance</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Retention Period (Days)</label>
            <input
              type="number"
              value={safeInt(settings.data_retention_days, 365)}
              onChange={(e) => handleSettingChange('data_retention_days', safeInt(e.target.value, 365))}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              min={30}
              max={3650}
            />
            <p className="text-xs text-gray-500 mt-1">Closed tickets will be archived after this period</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Backup Frequency</label>
            <select
              value={settings.backup_frequency ?? 'daily'}
              onChange={(e) => handleSettingChange('backup_frequency', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!settings.maintenance_mode}
                onChange={(e) => handleSettingChange('maintenance_mode', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Enable maintenance mode</span>
            </label>
            <p className="text-xs text-gray-500 ml-6">Only administrators will be able to access the system</p>
          </div>
        </div>
      </section>

      {/* Save Actions */}
      <div className="flex items-center justify-between bg-gray-50 rounded-2xl p-4">
        <div className="text-sm text-gray-600">Changes will be applied immediately after saving.</div>

        <div className="flex items-center gap-3">
          <Button variant="ghost" leftIcon={<RefreshCw className="w-4 h-4" />} onClick={handleReset}>
                        Reset
          </Button>

          <Button
            variant="primary"
            leftIcon={<Save className="w-4 h-4" />}
            onClick={handleSave}
            loading={updateSettingsMutation.isLoading}
          >
                        Save Settings
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default SystemSettings;
